import { TextField, type TextFieldProps } from '@mui/material'
import _, { type PropertyPath } from 'lodash'
import {
  type ChangeEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSearch } from '@/features/search/hooks/useSearch'
import { useAppSelector } from '@/redux/hooks'
import type { PropertyStringPath, SearchFilterState } from '../../types'

type Props = {
  fieldPath: PropertyPath & PropertyStringPath<SearchFilterState>
  debounceDelay?: number
} & TextFieldProps

export default function FilterTextField(props: Props) {
  const { fieldPath, debounceDelay = 333, ...rest } = props
  const { selectors, updateNext } = useSearch()
  const nextSearchOptions = useAppSelector(selectors.selectNextSearchParameters)
  const stateValue = useMemo(
    () => _.get(nextSearchOptions, fieldPath) ?? '',
    [nextSearchOptions, fieldPath],
  )
  const [typing, setTyping] = useState(false)
  const [localValue, setLocalValue] = useState(stateValue)
  const timeoutRef = useRef(-1)

  useEffect(
    function refreshWithStateValue() {
      if (!typing && stateValue !== localValue) {
        setLocalValue(stateValue)
      }
    },
    [typing, stateValue, localValue],
  )

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current)
  }, [])

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const newValue = event.target.value
      setLocalValue(newValue)
      setTyping(true)

      clearTimeout(timeoutRef.current)

      timeoutRef.current = setTimeout(() => {
        setTyping(false)
        const temp = {}
        _.set(temp, fieldPath, newValue)
        updateNext(temp)
      }, debounceDelay)
    },
    [updateNext, debounceDelay, fieldPath],
  )

  return (
    <TextField
      fullWidth
      {...rest}
      value={localValue}
      onChange={handleInputChange}
    />
  )
}
